import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/ApiService';
import {
  BILT_MARK_MONO_SRC,
  QR_STICKER_TEMPLATES,
  renderQrSticker,
  type QrStickerTemplate,
} from '../../utils/qrStickerCanvas';
import { loadImageSafe } from '../../utils/shareCardCanvas';

export interface QrStickerGeneratorProps {
  isOpen: boolean;
  onClose: () => void;
  /** `panel` embeds inside the Share modal without a nested dialog. */
  variant?: 'modal' | 'panel';
  vehicleName: string;
  /** Full public share URL for the vehicle. */
  shareUrl: string;
  /** URL of the app's existing QR image for this vehicle (from generate_qr.php). */
  qrImageUrl: string;
  vehicleId?: number;
  /** Optional vehicle photo; only used on event card and show board templates. */
  vehicleImageUrl?: string | null;
}

interface AccentChoice {
  name: string;
  value: string | null;
}

/** Approved accent palette. Accents never touch the QR quiet zone. */
const ACCENT_PALETTE: AccentChoice[] = [
  { name: 'Neutral', value: null },
  { name: 'Orange', value: '#f97316' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Purple', value: '#a855f7' },
];

const TEMPLATE_ORDER: QrStickerTemplate[] = [
  'square_sticker',
  'windshield',
  'event_card',
  'show_board',
];

const QrStickerGenerator: React.FC<QrStickerGeneratorProps> = ({
  isOpen,
  onClose,
  variant = 'modal',
  vehicleName,
  shareUrl,
  qrImageUrl,
  vehicleId,
  vehicleImageUrl,
}) => {
  const { showToast } = useToast();

  const [template, setTemplate] = useState<QrStickerTemplate>('square_sticker');
  const [accent, setAccent] = useState<string | null>(null);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [qrMissing, setQrMissing] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [renderFailed, setRenderFailed] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const qrImgRef = useRef<HTMLImageElement | null>(null);
  const vehicleImgRef = useRef<HTMLImageElement | null>(null);
  const brandMarkRef = useRef<HTMLImageElement | null>(null);
  const blobRef = useRef<Blob | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const shortUrl = shareUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
  const spec = QR_STICKER_TEMPLATES[template];

  // Load the QR image (required) and vehicle photo (optional) once per open.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setAssetsLoaded(false);
    setQrMissing(false);
    Promise.all([
      loadImageSafe(qrImageUrl),
      vehicleImageUrl ? loadImageSafe(vehicleImageUrl) : Promise.resolve(null),
      loadImageSafe(BILT_MARK_MONO_SRC),
    ]).then(([qrImg, vehicleImg, brandMark]) => {
      if (cancelled) return;
      qrImgRef.current = qrImg;
      vehicleImgRef.current = vehicleImg;
      brandMarkRef.current = brandMark;
      setQrMissing(!qrImg);
      setAssetsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, qrImageUrl, vehicleImageUrl]);

  // Re-render the preview whenever selections or loaded assets change.
  useEffect(() => {
    if (!isOpen || !assetsLoaded || !qrImgRef.current) return undefined;
    let cancelled = false;
    setRendering(true);
    setRenderFailed(false);

    renderQrSticker({
      template,
      vehicleName,
      shortUrl,
      qrImage: qrImgRef.current,
      brandMark: brandMarkRef.current,
      accentColor: accent,
      vehicleImage: vehicleImgRef.current,
    })
      .then((blob) => {
        if (cancelled) return;
        blobRef.current = blob;
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const url = URL.createObjectURL(blob);
        previewUrlRef.current = url;
        setPreviewUrl(url);
      })
      .catch(() => {
        if (cancelled) return;
        setRenderFailed(true);
        showToast('Could not render the sticker. Please try again.', 'error', {
          dedupeKey: 'qr-sticker-render-failed',
        });
      })
      .finally(() => {
        if (!cancelled) setRendering(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- name/url props are stable per open
  }, [isOpen, assetsLoaded, template, accent]);

  // Clean up the preview object URL when the modal closes or unmounts.
  useEffect(() => {
    if (isOpen) return undefined;
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      blobRef.current = null;
      setPreviewUrl(null);
    };
  }, [isOpen]);

  const fileName = useMemo(() => {
    const slug = vehicleName.replace(/\s+/g, '-').toLowerCase().slice(0, 40);
    return `bilt-qr-${template}-${slug}.png`;
  }, [vehicleName, template]);

  const handleDownload = useCallback(() => {
    const blob = blobRef.current;
    if (!blob) return;
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
    void apiService.trackEvent('qr_sticker_generated', {
      vehicle_id: vehicleId,
      metadata: { template, accent: accent ?? 'neutral' },
    });
    showToast('Sticker downloaded.', 'success');
  }, [fileName, vehicleId, template, accent, showToast]);

  const busy = rendering || !assetsLoaded;

  if (!isOpen) return null;

  const content = qrMissing ? (
    <div className="rounded-xl border border-white/[0.06] bg-[#0a0a0a]/60 p-8 text-center">
      <p className="text-sm text-red-300 mb-2">Could not load the QR code image.</p>
      <p className="text-sm text-gray-400">
        Generate a QR code for this vehicle from the QR Codes tab, then try again.
      </p>
    </div>
  ) : (
    <div className="grid gap-6 md:grid-cols-[300px,minmax(0,1fr)]">
      <div>
        <fieldset className="mb-6">
          <legend className="text-sm font-medium text-gray-300 mb-2">Template</legend>
          <div className="space-y-2">
            {TEMPLATE_ORDER.map((value) => {
              const t = QR_STICKER_TEMPLATES[value];
              return (
                <label key={value} className="block cursor-pointer">
                  <input
                    type="radio"
                    name="qr-sticker-template"
                    value={value}
                    checked={template === value}
                    onChange={() => setTemplate(value)}
                    className="sr-only peer"
                  />
                  <span className="block rounded-lg border border-white/[0.08] bg-[#0c0c0c]/50 px-3 py-2 transition-colors hover:border-white/[0.18] peer-checked:border-orange-500 peer-checked:bg-orange-500/10 peer-focus-visible:ring-2 peer-focus-visible:ring-orange-500">
                    <span className="block text-sm font-medium text-white">{t.label}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      {t.useCase} · {t.physicalSize} at 300 DPI
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="mb-6">
          <legend className="text-sm font-medium text-gray-300 mb-2">Accent color</legend>
          <div className="grid grid-cols-3 gap-2">
            {ACCENT_PALETTE.map((choice) => (
              <label key={choice.name} className="cursor-pointer">
                <input
                  type="radio"
                  name="qr-sticker-accent"
                  value={choice.value ?? 'neutral'}
                  checked={accent === choice.value}
                  onChange={() => setAccent(choice.value)}
                  className="sr-only peer"
                />
                <span className="flex flex-col items-center gap-1.5 rounded-lg border border-white/[0.08] bg-[#0c0c0c]/50 px-2 py-2 transition-colors hover:border-white/[0.18] peer-checked:border-orange-500 peer-checked:bg-orange-500/10 peer-focus-visible:ring-2 peer-focus-visible:ring-orange-500">
                  <span
                    aria-hidden
                    className="w-6 h-6 rounded-full border border-white/[0.10]"
                    style={{ backgroundColor: choice.value ?? '#3f3f46' }}
                  />
                  <span className="text-xs text-gray-300">{choice.name}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <p className="text-xs text-gray-500">
          Print at 300 DPI. This template prints at {spec.physicalSize}. Accent color
          tints the bars, brand lockup, and build URL only. The QR modules stay black on
          white for reliable scanning, so do not crop the quiet zone.
        </p>
      </div>

      <div className="flex flex-col">
        <div
          className="flex items-center justify-center rounded-xl border border-white/[0.06] bg-[#0a0a0a]/60 p-4 min-h-[300px]"
          aria-live="polite"
        >
          {busy ? (
            <div role="status" className="flex flex-col items-center gap-3 text-gray-400">
              <span className="spinner-sm" aria-hidden />
              <span className="text-sm">Rendering preview…</span>
            </div>
          ) : renderFailed ? (
            <p className="text-sm text-red-300 text-center px-4">
              Preview failed to render. Adjust the options or try again.
            </p>
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt={`${spec.label} preview for ${vehicleName} with QR code`}
              className="max-h-[340px] max-w-full w-auto rounded-lg shadow-lg shadow-black/40"
            />
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2 mt-4">
          <Button
            variant="primary"
            onClick={handleDownload}
            disabled={busy || renderFailed || !previewUrl}
            loading={busy}
            loadingLabel="Rendering…"
          >
            Download PNG
          </Button>
        </div>
      </div>
    </div>
  );

  if (variant === 'panel') {
    return (
      <div className="space-y-3">
        <div>
          <h3 className="text-lg font-semibold text-white">QR sticker</h3>
          <p className="text-sm text-gray-400 mt-1">
            Printable vinyl templates with quiet-zone safe QR modules.
          </p>
        </div>
        {content}
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Printable QR sticker" size="xl">
      {content}
    </Modal>
  );
};

export default QrStickerGenerator;
